// ============================================================
// FLUSH FASTENERS — Shopify AJAX cart (same-origin only)
// ============================================================
// Depends on being hosted on flushfasteners.com (or any domain
// that serves Shopify's storefront at /cart/add.js, /cart.js,
// /cart/change.js, /products/{handle}.js).
//
// Exposes window.FFCart with:
//   init(handles)    pre-fetch products + build SKU→variantId map
//   add(sku, qty)    add 1+ units of a SKU to the Shopify cart
//   open() / close() drawer controls
//   refresh()        refetch /cart.js and re-render drawer
//   hasVariant(sku)  returns true if SKU is mapped to a variant
//
// The drawer + header cart badge are injected into the DOM on init.
// ============================================================

(function () {
  const SHOP_ORIGIN = 'https://flushfasteners.com';
  const LIVE = location.hostname === 'flushfasteners.com' || location.hostname.endsWith('.flushfasteners.com');

  const state = {
    skuMap: {},        // { SKU: variantId }
    productData: {},   // { handle: productJson }
    cart: null,        // last /cart.js payload
    ready: false,
    inflight: false,
  };

  // ----- UTIL -----
  function $(sel, root) { return (root || document).querySelector(sel); }
  function fmt(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // Same-origin fetch helper; dev-mode fallback uses absolute SHOP_ORIGIN (only works if CORS is permissive — Shopify's /products/{h}.js is CORS-open, but /cart/* is not).
  function shopFetch(path, opts) {
    const url = LIVE ? path : (SHOP_ORIGIN + path);
    return fetch(url, opts);
  }

  // ----- PRODUCT / SKU MAP -----
  async function loadProduct(handle) {
    try {
      const res = await shopFetch('/products/' + handle + '.js');
      if (!res.ok) return null;
      const p = await res.json();
      state.productData[handle] = p;
      (p.variants || []).forEach(v => {
        if (v.sku) state.skuMap[v.sku] = v.id;
      });
      return p;
    } catch (e) {
      console.warn('[FFCart] product load failed for', handle, e);
      return null;
    }
  }

  async function init(handles) {
    if (state.ready) return;
    injectStyles();
    injectDrawer();
    // Load products in parallel
    await Promise.all((handles || []).map(loadProduct));
    // Load current cart (so the badge reflects existing items)
    try {
      const res = await shopFetch('/cart.js');
      if (res.ok) state.cart = await res.json();
    } catch (e) { /* cart unreachable off-prod — silent */ }
    state.ready = true;
    renderBadge();
    renderDrawer();
    // Notify subscribers (sku-table.js) so it can reveal Buy buttons
    document.dispatchEvent(new CustomEvent('ffcart:ready', { detail: { skus: Object.keys(state.skuMap) } }));
  }

  // ----- CART OPS -----
  async function add(sku, qty) {
    const id = state.skuMap[sku];
    if (!id) {
      console.warn('[FFCart] no variant for SKU', sku);
      return { ok: false, reason: 'no-variant' };
    }
    return addVariant(id, qty, { sku });
  }

  async function addVariant(id, qty, meta) {
    if (!id) return { ok: false, reason: 'no-variant' };
    if (state.inflight) return { ok: false, reason: 'busy' };
    state.inflight = true;
    try {
      if (!LIVE) {
        // Off-production simulation: fabricate a cart line so the drawer demos correctly.
        const p = Object.values(state.productData).find(p => (p.variants || []).some(v => v.id === id));
        const v = p && p.variants.find(v => v.id === id);
        if (!state.cart) state.cart = { items: [], item_count: 0, total_price: 0 };
        const existing = state.cart.items.find(it => it.variant_id === id);
        if (existing) {
          existing.quantity += (qty || 1);
          existing.final_line_price = existing.price * existing.quantity;
        } else {
          state.cart.items.push({
            id: id,
            variant_id: id,
            key: 'mock-' + id,
            sku: (meta && meta.sku) || (v && v.sku) || '',
            quantity: qty || 1,
            price: v ? v.price : 0,
            final_line_price: (v ? v.price : 0) * (qty || 1),
            product_title: p ? p.title : sku,
            variant_title: v ? v.public_title : '',
            image: p && p.featured_image,
          });
        }
        state.cart.item_count = state.cart.items.reduce((a, it) => a + it.quantity, 0);
        state.cart.total_price = state.cart.items.reduce((a, it) => a + it.final_line_price, 0);
      } else {
        const res = await shopFetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: id, quantity: qty || 1 }] }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          console.warn('[FFCart] add failed', err);
          state.inflight = false;
          return { ok: false, reason: err.description || 'add-failed' };
        }
        // Refresh cart
        const cartRes = await shopFetch('/cart.js');
        state.cart = await cartRes.json();
      }
      renderBadge();
      renderDrawer();
      openDrawer();
      state.inflight = false;
      return { ok: true };
    } catch (e) {
      console.warn('[FFCart] add error', e);
      state.inflight = false;
      return { ok: false, reason: 'network' };
    }
  }

  async function change(key, qty) {
    if (state.inflight) return;
    state.inflight = true;
    try {
      if (!LIVE) {
        const it = state.cart.items.find(x => x.key === key);
        if (it) {
          if (qty <= 0) {
            state.cart.items = state.cart.items.filter(x => x.key !== key);
          } else {
            it.quantity = qty;
            it.final_line_price = it.price * qty;
          }
          state.cart.item_count = state.cart.items.reduce((a, it) => a + it.quantity, 0);
          state.cart.total_price = state.cart.items.reduce((a, it) => a + it.final_line_price, 0);
        }
      } else {
        const res = await shopFetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty }),
        });
        if (res.ok) state.cart = await res.json();
      }
      renderBadge();
      renderDrawer();
    } catch (e) { console.warn('[FFCart] change error', e); }
    state.inflight = false;
  }

  function goCheckout() {
    if (!LIVE) {
      alert('Checkout flows live at flushfasteners.com/checkout. This is a preview environment — in production this button takes the customer straight to Shopify checkout on your own domain.');
      return;
    }
    location.href = '/checkout';
  }

  // ----- STYLES -----
  function injectStyles() {
    if ($('#ffcart-styles')) return;
    const s = document.createElement('style');
    s.id = 'ffcart-styles';
    s.textContent = `
      /* Cart icon on nav — borderless */
      .ffcart-trigger {
        position: relative;
        display: inline-flex; align-items: center; justify-content: center;
        width: 36px; height: 36px; padding: 0;
        border: 0; background: transparent;
        color: var(--fg);
        cursor: pointer;
        transition: color 200ms var(--ease), transform 200ms var(--ease);
      }
      .ffcart-trigger:hover { color: var(--brand-500, #d8dce0); transform: translateY(-1px); }
      .ffcart-trigger svg { width: 22px; height: 22px; display: block; }
      .ffcart-trigger-count {
        position: absolute; top: -2px; right: -4px;
        display: inline-flex; align-items: center; justify-content: center;
        min-width: 16px; height: 16px; padding: 0 4px;
        font-size: 9px; letter-spacing: 0;
        background: var(--fg); color: var(--bg);
        border-radius: 999px;
        font-family: var(--font-mono);
        line-height: 1;
      }
      .ffcart-trigger-count.hidden { display: none; }

      /* Drawer */
      .ffcart-overlay {
        position: fixed; inset: 0; z-index: 200;
        background: rgba(0,0,0,0.6);
        opacity: 0; pointer-events: none;
        transition: opacity 280ms var(--ease);
      }
      .ffcart-overlay.open { opacity: 1; pointer-events: auto; }
      .ffcart-drawer {
        position: fixed; top: 0; right: 0; bottom: 0; z-index: 201;
        width: min(440px, 92vw);
        background: var(--bg-alt, #0d0d10);
        border-left: 1px solid var(--stroke, #22242a);
        color: var(--fg, #f3f3f1);
        display: flex; flex-direction: column;
        transform: translateX(100%);
        transition: transform 320ms cubic-bezier(0.22, 0.61, 0.36, 1);
      }
      .ffcart-drawer.open { transform: translateX(0); }
      .ffcart-drawer header {
        display: flex; justify-content: space-between; align-items: center;
        padding: 26px 28px; border-bottom: 1px solid var(--stroke);
      }
      .ffcart-drawer header .title {
        font-family: var(--font-editorial, serif);
        font-weight: 300;
        font-size: 28px;
        letter-spacing: -0.01em;
      }
      .ffcart-drawer header .title em { font-style: italic; }
      .ffcart-drawer header .close {
        background: none; border: none; color: var(--fg-muted);
        font-size: 22px; cursor: pointer; line-height: 1;
        padding: 4px 8px;
      }
      .ffcart-drawer header .close:hover { color: var(--fg); }

      .ffcart-items {
        flex: 1; overflow-y: auto;
        padding: 8px 28px;
      }
      .ffcart-empty {
        padding: 80px 28px; text-align: center;
        color: var(--fg-muted);
      }
      .ffcart-empty .eyebrow {
        font-family: var(--font-mono);
        font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase;
        color: var(--fg-dim); margin-bottom: 14px;
      }
      .ffcart-empty h4 {
        font-family: var(--font-editorial, serif);
        font-weight: 300; font-size: 26px;
        margin-bottom: 12px; color: var(--fg);
      }
      .ffcart-empty p { font-size: 14px; line-height: 1.6; }

      .ffcart-item {
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 8px 16px;
        padding: 22px 0;
        border-bottom: 1px solid var(--stroke);
      }
      .ffcart-item:last-child { border-bottom: none; }
      .ffcart-item .name {
        font-family: var(--font-body);
        font-size: 15px;
        color: var(--fg);
        line-height: 1.4;
        margin-bottom: 4px;
      }
      .ffcart-item .variant {
        font-family: var(--font-mono);
        font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase;
        color: var(--fg-dim);
      }
      .ffcart-item .price {
        font-family: var(--font-mono);
        font-size: 13px;
        color: var(--fg);
        text-align: right;
        white-space: nowrap;
      }
      .ffcart-item .qty {
        grid-column: 1 / 2;
        display: inline-flex; align-items: center; gap: 0;
        margin-top: 10px;
        border: 1px solid var(--stroke-strong);
        border-radius: 2px;
        width: fit-content;
      }
      .ffcart-item .qty button {
        background: none; border: none; color: var(--fg);
        width: 30px; height: 28px; cursor: pointer;
        font-family: var(--font-mono); font-size: 14px;
        transition: background 160ms var(--ease);
      }
      .ffcart-item .qty button:hover { background: rgba(255,255,255,0.06); }
      .ffcart-item .qty .count {
        min-width: 110px; text-align: center;
        font-family: var(--font-mono); font-size: 10px;
        letter-spacing: 0.06em;
        color: var(--fg);
        padding: 0 10px;
      }
      .ffcart-item .unit-note {
        font-family: var(--font-mono); font-size: 10px;
        letter-spacing: 0.1em;
        color: var(--fg-dim);
        margin-top: 4px;
      }
      .ffcart-item .remove {
        grid-column: 2 / 3;
        margin-top: 10px;
        background: none; border: none;
        font-family: var(--font-mono); font-size: 10px;
        letter-spacing: 0.18em; text-transform: uppercase;
        color: var(--fg-dim); cursor: pointer;
        padding: 0; align-self: end;
      }
      .ffcart-item .remove:hover { color: var(--fg); }

      .ffcart-foot {
        padding: 22px 28px 28px;
        border-top: 1px solid var(--stroke);
        background: var(--bg-alt);
      }
      .ffcart-foot .row {
        display: flex; justify-content: space-between; align-items: baseline;
        margin-bottom: 6px;
      }
      .ffcart-foot .row.subtotal {
        margin-bottom: 4px;
      }
      .ffcart-foot .row.subtotal .label {
        font-family: var(--font-mono); font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase;
        color: var(--fg-dim);
      }
      .ffcart-foot .row.subtotal .val {
        font-family: var(--font-editorial, serif);
        font-weight: 300; font-size: 26px;
        color: var(--fg);
      }
      .ffcart-foot .note {
        font-family: var(--font-mono); font-size: 10px;
        letter-spacing: 0.14em; text-transform: uppercase;
        color: var(--fg-dim);
        margin-bottom: 16px;
      }
      .ffcart-foot .checkout {
        display: block; width: 100%;
        padding: 16px;
        background: var(--fg); color: var(--bg);
        border: none; cursor: pointer;
        font-family: var(--font-body); font-size: 14px;
        letter-spacing: 0.02em;
        border-radius: 2px;
        text-align: center;
        transition: background 180ms var(--ease);
      }
      .ffcart-foot .checkout:hover { background: #fff; }
      .ffcart-foot .checkout[disabled] { opacity: 0.4; cursor: not-allowed; }

      /* Toast after add */
      .ffcart-toast {
        position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%) translateY(20px);
        z-index: 300;
        background: var(--fg); color: var(--bg);
        padding: 12px 20px;
        font-family: var(--font-mono); font-size: 11px;
        letter-spacing: 0.18em; text-transform: uppercase;
        border-radius: 2px;
        opacity: 0; pointer-events: none;
        transition: opacity 240ms var(--ease), transform 240ms var(--ease);
      }
      .ffcart-toast.show { opacity: 1; transform: translateX(-50%) translateY(0); }
    `;
    document.head.appendChild(s);
  }

  // ----- HEADER BADGE (replaces Request Quote button) -----
  function mountBadge() {
    const navCta = $('.ff-nav-cta');
    if (!navCta) return;
    if ($('.ffcart-trigger')) return;
    const btn = document.createElement('button');
    btn.className = 'ffcart-trigger';
    btn.type = 'button';
    btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 7h12l-1.2 10.2a2 2 0 0 1-2 1.8H9.2a2 2 0 0 1-2-1.8L6 7Z"/><path d="M9 7V5.5a3 3 0 0 1 6 0V7"/></svg><span class="ffcart-trigger-count hidden" id="ffcart-count" aria-label="Cart items">0</span>';
    btn.setAttribute('aria-label', 'Cart');
    btn.addEventListener('click', openDrawer);
    navCta.parentElement.insertBefore(btn, navCta);
    // Keep Request Quote but lighter; OR remove it to avoid crowding.
    // We keep it so engineering inquiries still have a path, but shrink spacing.
    navCta.style.marginLeft = '8px';
  }

  function renderBadge() {
    mountBadge();
    const el = $('#ffcart-count');
    if (!el) return;
    const n = state.cart ? state.cart.item_count : 0;
    if (n > 0) {
      el.textContent = n;
      el.classList.remove('hidden');
    } else {
      el.classList.add('hidden');
    }
  }

  // ----- DRAWER -----
  function injectDrawer() {
    if ($('.ffcart-drawer')) return;
    const overlay = document.createElement('div');
    overlay.className = 'ffcart-overlay';
    overlay.addEventListener('click', closeDrawer);
    const drawer = document.createElement('aside');
    drawer.className = 'ffcart-drawer';
    drawer.innerHTML = `
      <header>
        <div class="title">Your <em>cart.</em></div>
        <button class="close" aria-label="Close">×</button>
      </header>
      <div class="ffcart-items" id="ffcart-items"></div>
      <div class="ffcart-foot" id="ffcart-foot"></div>
    `;
    drawer.querySelector('.close').addEventListener('click', closeDrawer);
    document.body.appendChild(overlay);
    document.body.appendChild(drawer);

    const toast = document.createElement('div');
    toast.className = 'ffcart-toast';
    toast.textContent = 'Added to cart';
    document.body.appendChild(toast);
  }

  function renderDrawer() {
    const items = $('#ffcart-items');
    const foot = $('#ffcart-foot');
    if (!items || !foot) return;
    const cart = state.cart;
    if (!cart || !cart.items || cart.items.length === 0) {
      items.innerHTML = `
        <div class="ffcart-empty">
          <div class="eyebrow">§ Empty cart</div>
          <h4>No parts in your cart yet.</h4>
          <p>Add SKUs from any product page. Drawings &amp; STEP files download free, no cart required.</p>
        </div>`;
      foot.innerHTML = '';
      return;
    }
    items.innerHTML = cart.items.map(it => {
      const packs = Math.max(1, Math.round(it.quantity / 10));
      const perUnit = (it.price / 100).toFixed(2);
      return `
      <div class="ffcart-item" data-key="${it.key}">
        <div>
          <div class="name">${it.product_title || it.title || ''}</div>
          <div class="variant">${it.sku || ''}${it.variant_title ? ' · ' + it.variant_title : ''}</div>
          <div class="unit-note">$${perUnit} ea × ${it.quantity} units</div>
        </div>
        <div class="price">${fmt(it.final_line_price || it.price * it.quantity)}</div>
        <div class="qty">
          <button data-act="dec">−</button>
          <span class="count">${packs} pack${packs === 1 ? '' : 's'} of 10</span>
          <button data-act="inc">+</button>
        </div>
        <button class="remove" data-act="rm">Remove</button>
      </div>
    `;}).join('');
    items.querySelectorAll('.ffcart-item').forEach(row => {
      const key = row.dataset.key;
      const it = cart.items.find(i => i.key === key);
      row.querySelectorAll('[data-act]').forEach(b => {
        b.addEventListener('click', () => {
          if (b.dataset.act === 'inc') change(key, it.quantity + 10);
          if (b.dataset.act === 'dec') change(key, Math.max(0, it.quantity - 10));
          if (b.dataset.act === 'rm') change(key, 0);
        });
      });
    });
    foot.innerHTML = `
      <div class="row subtotal">
        <span class="label">Subtotal</span>
        <span class="val">${fmt(cart.total_price || 0)}</span>
      </div>
      <div class="note">Shipping &amp; tax calculated at checkout · ${cart.item_count} item${cart.item_count === 1 ? '' : 's'}</div>
      <button class="checkout" id="ffcart-checkout">Checkout →</button>
    `;
    foot.querySelector('#ffcart-checkout').addEventListener('click', goCheckout);
  }

  function openDrawer() {
    $('.ffcart-overlay').classList.add('open');
    $('.ffcart-drawer').classList.add('open');
    document.body.style.overflow = 'hidden';
  }
  function closeDrawer() {
    $('.ffcart-overlay').classList.remove('open');
    $('.ffcart-drawer').classList.remove('open');
    document.body.style.overflow = '';
  }

  function toast(msg) {
    const t = $('.ffcart-toast');
    if (!t) return;
    t.textContent = msg;
    t.classList.add('show');
    clearTimeout(t._to);
    t._to = setTimeout(() => t.classList.remove('show'), 1800);
  }

  function hasVariant(sku) { return !!state.skuMap[sku]; }

  window.FFCart = {
    init,
    add,
    addVariant,
    getProduct: (handle) => state.productData[handle] || null,
    open: openDrawer,
    close: closeDrawer,
    refresh: async () => {
      try {
        const res = await shopFetch('/cart.js');
        if (res.ok) state.cart = await res.json();
        renderBadge(); renderDrawer();
      } catch (e) {}
    },
    hasVariant,
    toast,
    _state: state,
  };
})();
